// src/contexts/AssistantContext.js
import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { fetchModels, FALLBACK_MODELS } from '../utils/llmModels';

const AssistantContext = createContext();

export const useAssistantContext = () => {
  return useContext(AssistantContext);
};

export const AssistantProvider = ({ children }) => {
  // --- Défilement automatique ---
  const [autoScroll, setAutoScroll] = useState(false);
  const toggleAutoScroll = () => setAutoScroll(v => !v);

  // --- Choix du modèle IA (liste fournie par le registre backend) ---
  const { token } = useAuth();
  const [models, setModels] = useState(FALLBACK_MODELS);
  const [modelId, setModelIdState] = useState('');
  const [modelSwitchMessage, setModelSwitchMessage] = useState('');
  const initialisedRef = useRef(false);

  // Chargement à la connexion seulement : l'endpoint exige un JWT.
  // Le modèle repart systématiquement sur le défaut du serveur (Mistral Medium) au
  // démarrage et à chaque connexion ; le choix ne vaut donc que pour la session.
  useEffect(() => {
    if (!token) {
      initialisedRef.current = false; // déconnexion : réarmer pour la prochaine session
      setModelIdState('');            // la prochaine session repart sur le défaut serveur
      return;
    }
    if (initialisedRef.current) return; // rafraîchissement du JWT : ne pas réinitialiser
    initialisedRef.current = true;
    // Pas d'annulation au démontage : en StrictMode l'effet est monté deux fois et
    // seule la première passe déclenche l'appel ; l'annuler laisserait la liste sur
    // le repli à un seul modèle, donc le sélecteur masqué au rechargement de page.
    fetchModels().then(({ models: list, default: defaultId, ok }) => {
      setModels(list);
      setModelIdState(defaultId);
      // Registre injoignable : réarmer pour retenter au prochain changement de token
      if (!ok) initialisedRef.current = false;
    });
  }, [token]);

  const selectedModel = useMemo(
    () => models.find(m => m.id === modelId) || models[0],
    [models, modelId]
  );

  const setModelId = useCallback((id) => {
    const model = models.find(m => m.id === id);
    if (!model) return;
    setModelIdState(id);
    // Avertissement RGPD pour les modèles hébergés hors Union européenne
    const message = model.sovereign
      ? `Modèle IA basculé sur ${model.label}`
      : `${model.label} est hébergé hors UE : ne saisissez aucune donnée personnelle ni sensible.`;
    setModelSwitchMessage(message);
    setTimeout(() => setModelSwitchMessage(''), model.sovereign ? 4000 : 6000);
  }, [models]);

  const value = {
    // Défilement automatique
    autoScroll,
    toggleAutoScroll,
    // Modèle IA
    models,
    modelId: selectedModel?.id,
    selectedModel,
    setModelId,
    modelSwitchMessage,
  };

  return (
    <AssistantContext.Provider value={value}>
      {children}
    </AssistantContext.Provider>
  );
};
